import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Popconfirm,
  message,
  Row,
  Col,
  Statistic,
  Typography,
  Avatar,
  Rate,
  DatePicker,
  Upload,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  PhoneOutlined,
  CarOutlined,
  StarOutlined,
  EyeOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

interface Driver {
  id: string;
  name: string;
  phone: string;
  email: string;
  licenseNumber: string;
  licenseType: string;
  status: 'available' | 'busy' | 'offline' | 'suspended';
  rating: number;
  totalOrders: number;
  joinDate: string;
  currentVehicle?: string;
  currentOrder?: string;
  experience: number; // 年
  address: string;
  emergencyContact: string;
  emergencyPhone: string;
  avatar?: string;
  documents: {
    license: string;
    idCard: string;
    healthCert: string;
  };
}

const DriverManagement: React.FC = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewingDriver, setViewingDriver] = useState<Driver | null>(null);
  const [form] = Form.useForm();

  // シミュレート数据
  useEffect(() => {
    setDrivers([
      {
        id: '1',
        name: '王师傅',
        phone: '13800138001',
        email: 'wangsifu@example.com',
        licenseNumber: 'A1234567890',
        licenseType: 'C1',
        status: 'available',
        rating: 4.8,
        totalOrders: 1250,
        joinDate: '2022-03-15',
        experience: 8,
        address: '北京市朝阳区XX街道XX号',
        emergencyContact: '张女士',
        emergencyPhone: '13900139001',
        documents: {
          license: 'license1.jpg',
          idCard: 'id1.jpg',
          healthCert: 'health1.jpg'
        }
      },
      {
        id: '2',
        name: '李师傅',
        phone: '13800138002',
        email: 'lisifu@example.com',
        licenseNumber: 'A2345678901',
        licenseType: 'C1',
        status: 'busy',
        rating: 4.6,
        totalOrders: 890,
        joinDate: '2022-06-20',
        currentVehicle: '奔驰E300',
        currentOrder: 'O20231201002',
        experience: 5,
        address: '北京市海淀区YY街道YY号',
        emergencyContact: '王先生',
        emergencyPhone: '13900139002',
        documents: {
          license: 'license2.jpg',
          idCard: 'id2.jpg',
          healthCert: 'health2.jpg'
        }
      },
      {
        id: '3',
        name: '张师傅',
        phone: '13800138003',
        email: 'zhangsifu@example.com',
        licenseNumber: 'A3456789012',
        licenseType: 'B2',
        status: 'offline',
        rating: 4.9,
        totalOrders: 2100,
        joinDate: '2021-11-10',
        experience: 12,
        address: '北京市西城区ZZ街道ZZ号',
        emergencyContact: '刘女士',
        emergencyPhone: '13900139003',
        documents: {
          license: 'license3.jpg',
          idCard: 'id3.jpg',
          healthCert: 'health3.jpg'
        }
      }
    ]);
  }, []);

  const statusColors = {
    available: 'success',
    busy: 'processing',
    offline: 'default',
    suspended: 'error'
  };

  const statusLabels = {
    available: '可接单',
    busy: '服务中',
    offline: '离线',
    suspended: '已暂停'
  };

  const columns = [
    {
      title: 'ドライバー情報',
      key: 'driver',
      render: (_: any, record: Driver) => (
        <Space>
          <Avatar src={record.avatar} icon={<UserOutlined />} />
          <div>
            <div><strong>{record.name}</strong></div>
            <div style={{ color: '#666', fontSize: '12px' }}>
              <PhoneOutlined style={{ marginRight: 4 }} />
              {record.phone}
            </div>
          </div>
        </Space>
      )
    },
    {
      title: '驾照情報',
      key: 'license',
      render: (_: any, record: Driver) => (
        <div>
          <div>{record.licenseNumber}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.licenseType}类</div>
        </div>
      )
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      render: (status: keyof typeof statusLabels) => (
        <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>
      )
    },
    {
      title: '评分',
      dataIndex: 'rating',
      key: 'rating',
      render: (rating: number) => (
        <Space>
          <Rate disabled defaultValue={rating} />
          <span>{rating}</span>
        </Space>
      )
    },
    {
      title: '完成注文',
      dataIndex: 'totalOrders',
      key: 'totalOrders'
    },
    {
      title: '驾龄',
      dataIndex: 'experience',
      key: 'experience',
      render: (years: number) => `${years}年`
    },
    {
      title: '当前車両',
      dataIndex: 'currentVehicle',
      key: 'currentVehicle',
      render: (vehicle: string) => vehicle || '未分配'
    },
    {
      title: '入职時間',
      dataIndex: 'joinDate',
      key: 'joinDate'
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: Driver) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
詳細
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
編集
          </Button>
          {record.status !== 'suspended' && (
            <Button
              type="link"
              danger
              onClick={() => handleSuspend(record.id)}
            >
一時停止
            </Button>
          )}
          {record.status === 'suspended' && (
            <Button
              type="link"
              onClick={() => handleRestore(record.id)}
            >
復旧
            </Button>
          )}
          <Popconfirm
            title="このドライバーを削除しますか？"
            onConfirm={() => handleDelete(record.id)}
            okText="確認"
            cancelText="キャンセル"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
削除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const handleAdd = () => {
    setEditingDriver(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (driver: Driver) => {
    setEditingDriver(driver);
    form.setFieldsValue(driver);
    setModalVisible(true);
  };

  const handleView = (driver: Driver) => {
    setViewingDriver(driver);
    setViewModalVisible(true);
  };

  const handleDelete = (id: string) => {
    setDrivers(prev => prev.filter(item => item.id !== id));
    message.success('削除しました');
  };

  const handleSuspend = (id: string) => {
    setDrivers(prev => prev.map(item => 
      item.id === id 
        ? { ...item, status: 'suspended' }
        : item
    ));
    message.success('ドライバーを一時停止しました');
  };

  const handleRestore = (id: string) => {
    setDrivers(prev => prev.map(item => 
      item.id === id 
        ? { ...item, status: 'available' }
        : item
    ));
    message.success('ドライバーを復旧しました');
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingDriver) {
        // 編集
        setDrivers(prev => prev.map(item => 
          item.id === editingDriver.id 
            ? { ...item, ...values }
            : item
        ));
        message.success('更新しました');
      } else {
        // 新規追加
        const newDriver: Driver = {
          id: Date.now().toString(),
          ...values,
          rating: 5.0,
          totalOrders: 0,
          joinDate: new Date().toISOString().split('T')[0],
          documents: {
            license: '',
            idCard: '',
            healthCert: ''
          }
        };
        setDrivers(prev => [newDriver, ...prev]);
        message.success('追加しました');
      }
      
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('フォームのバリデーションに失敗しました:', error);
    }
  };

  const totalStats = {
    totalDrivers: drivers.length,
    availableDrivers: drivers.filter(d => d.status === 'available').length,
    busyDrivers: drivers.filter(d => d.status === 'busy').length,
    averageRating: drivers.reduce((sum, d) => sum + d.rating, 0) / drivers.length
  };

  return (
    <div>
      <Title level={2}>ドライバー管理</Title>
      
      {/* 統計カード */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="ドライバー総数"
              value={totalStats.totalDrivers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="受付可能"
              value={totalStats.availableDrivers}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="サービス中"
              value={totalStats.busyDrivers}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均評価"
              value={totalStats.averageRating}
              precision={1}
              prefix={<StarOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* ドライバーリスト */}
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
ドライバーを追加
          </Button>
        </div>
        
        <Table
          columns={columns}
          dataSource={drivers}
          loading={loading}
          rowKey="id"
          pagination={{
            total: drivers.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]}件 / 全${total}件`
          }}
        />
      </Card>

      {/* 追加/編集モーダル */}
      <Modal
        title={editingDriver ? 'ドライバーの編集' : 'ドライバーの追加'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ status: 'available', licenseType: '普通' }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="氏名"
                rules={[{ required: true, message: 'ドライバーの氏名を入力してください' }]}
              >
                <Input placeholder="ドライバーの氏名を入力" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="電話番号"
                rules={[
                  { required: true, message: '電話番号を入力してください' },
                  { pattern: /^0\d{1,4}-\d{1,4}-\d{4}$|^0\d{9,10}$/, message: '正しい電話番号を入力してください' }
                ]}
              >
                <Input placeholder="電話番号を入力" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="メールアドレス"
                rules={[
                  { required: true, message: 'メールアドレスを入力してください' },
                  { type: 'email', message: '正しいメールアドレスを入力してください' }
                ]}
              >
                <Input placeholder="メールアドレスを入力" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="licenseNumber"
                label="免許証番号"
                rules={[{ required: true, message: '免許証番号を入力してください' }]}
              >
                <Input placeholder="免許証番号を入力" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="licenseType"
                label="免許証種別"
                rules={[{ required: true, message: '免許証種別を選択してください' }]}
              >
                <Select placeholder="免許証種別を選択">
                  <Option value="普通">普通免許</Option>
                  <Option value="中型">中型免許</Option>
                  <Option value="大型">大型免許</Option>
                  <Option value="特殊">特殊免許</Option>
                  <Option value="二輪">二輪免許</Option>
                  <Option value="原付">原付免許</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="experience"
                label="運転歴(年)"
                rules={[{ required: true, message: '運転歴を入力してください' }]}
              >
                <Input type="number" placeholder="運転歴を入力" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label="ステータス"
                rules={[{ required: true, message: 'ステータスを選択してください' }]}
              >
                <Select placeholder="ステータスを選択">
                  <Option value="available">受付可能</Option>
                  <Option value="offline">オフライン</Option>
                  <Option value="suspended">一時停止</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="address"
            label="住所"
            rules={[{ required: true, message: '住所を入力してください' }]}
          >
            <Input.TextArea placeholder="詳細な住所を入力" rows={2} />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="emergencyContact"
                label="緊急連絡先"
                rules={[{ required: true, message: '緊急連絡先を入力してください' }]}
              >
                <Input placeholder="緊急連絡先の氏名を入力" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="emergencyPhone"
                label="緊急連絡先電話"
                rules={[
                  { required: true, message: '緊急連絡先電話を入力してください' },
                  { pattern: /^0\d{1,4}-\d{1,4}-\d{4}$|^0\d{9,10}$/, message: '正しい電話番号を入力してください' }
                ]}
              >
                <Input placeholder="緊急連絡先電話を入力" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 詳細表示モーダル */}
      <Modal
        title="ドライバー詳細"
        open={viewModalVisible}
        footer={null}
        onCancel={() => setViewModalVisible(false)}
        width={600}
      >
        {viewingDriver && (
          <div>
            <Row gutter={16}>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Avatar size={80} src={viewingDriver.avatar} icon={<UserOutlined />} />
                  <div style={{ marginTop: 8 }}>
                    <Title level={4}>{viewingDriver.name}</Title>
                    <Tag color={statusColors[viewingDriver.status]}>
                      {statusLabels[viewingDriver.status]}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col span={16}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Row>
                    <Col span={8}><span style={{ fontWeight: 'bold' }}>電話番号：</span></Col>
                    <Col span={16}>{viewingDriver.phone}</Col>
                  </Row>
                  <Row>
                    <Col span={8}><span style={{ fontWeight: 'bold' }}>メール：</span></Col>
                    <Col span={16}>{viewingDriver.email}</Col>
                  </Row>
                  <Row>
                    <Col span={8}><span style={{ fontWeight: 'bold' }}>免許証番号：</span></Col>
                    <Col span={16}>{viewingDriver.licenseNumber}</Col>
                  </Row>
                  <Row>
                    <Col span={8}><span style={{ fontWeight: 'bold' }}>免許証種別：</span></Col>
                    <Col span={16}>{viewingDriver.licenseType}免許</Col>
                  </Row>
                  <Row>
                    <Col span={8}><span style={{ fontWeight: 'bold' }}>運転歴：</span></Col>
                    <Col span={16}>{viewingDriver.experience}年</Col>
                  </Row>
                  <Row>
                    <Col span={8}><span style={{ fontWeight: 'bold' }}>評価：</span></Col>
                    <Col span={16}>
                      <Rate disabled defaultValue={viewingDriver.rating} />
                      <span style={{ marginLeft: 8 }}>{viewingDriver.rating}</span>
                    </Col>
                  </Row>
                </Space>
              </Col>
            </Row>
            
            <Divider />
            
            <Row gutter={16}>
              <Col span={8}>
                <Statistic title="完了注文数" value={viewingDriver.totalOrders} />
              </Col>
              <Col span={8}>
                <Statistic title="入社日" value={viewingDriver.joinDate} />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="現在の車両" 
                  value={viewingDriver.currentVehicle || '未割当'} 
                  formatter={() => viewingDriver.currentVehicle || '未割当'}
                />
              </Col>
            </Row>
            
            <Divider />
            
            <div>
              <Title level={5}>連絡先情報</Title>
              <Row gutter={16}>
                <Col span={24}>
                  <span style={{ fontWeight: 'bold' }}>住所：</span> {viewingDriver.address}
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <span style={{ fontWeight: 'bold' }}>緊急連絡先：</span> {viewingDriver.emergencyContact}
                </Col>
                <Col span={12}>
                  <span style={{ fontWeight: 'bold' }}>緊急連絡先電話：</span> {viewingDriver.emergencyPhone}
                </Col>
              </Row>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DriverManagement;